/**
 * Cliente MCP mínimo del front (IHC, orden f13222da) — superficie same-origin con el JWT.
 *
 * El chat-agente usa las tools MCP server-side; aquí el FRONT llama UNA tool de LECTURA,
 * get_available_actions, para derivar los controles interactivos del HTML del chat por
 * PERMISO del actor (RBAC-filtrado server-side, madre resolverVerbosPorEntidad). NUNCA se
 * renderiza un verbo fuera de acciones_disponibles (daría 403). La EJECUCIÓN no va por
 * aquí: va por la API canónica de transiciones (lib/api), que reaplica RBAC+FSM+DMN.
 *
 * Transporte: POST /api/v1/mcp (Streamable HTTP, JSON-RPC enmarcado en SSE). El JWT del
 * usuario scopea la respuesta (mismo origen que la PWA).
 */

export interface AccionesActor {
  instancia_id: string
  entidad: string // nombre canónico AARPIA.<sub>.<token>.<estado>
  current_state: string
  current_phase: string
  acciones_disponibles: string[] // verbos YA filtrados por el permiso del actor
}

/** Llama una tool MCP de lectura y devuelve su `data` (parsea el SSE-framed JSON-RPC). */
async function mcpToolCall<T = unknown>(name: string, args: Record<string, unknown>, token: string | null): Promise<T | null> {
  const res = await fetch('/api/v1/mcp', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json, text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ jsonrpc: '2.0', method: 'tools/call', params: { name, arguments: args }, id: 1 }),
  })
  const texto = await res.text()
  // SSE-framed: la respuesta llega como `event: message\ndata: {…}\n\n`.
  const linea = texto.split('\n').find((l) => l.startsWith('data:'))
  if (!linea) return null
  let env: any
  try { env = JSON.parse(linea.slice(5).trim()) } catch { return null }
  if (env?.result?.isError) return null
  const text = env?.result?.content?.[0]?.text
  if (typeof text !== 'string') return null
  try { return JSON.parse(text) as T } catch { return null }
}

/** Verbos que ESTE actor puede ejecutar sobre la instancia EN SU ESTADO (RBAC-filtrado). */
export function getAvailableActions(instanciaId: string, token: string | null): Promise<AccionesActor | null> {
  return mcpToolCall<AccionesActor>('get_available_actions', { instancia_id: instanciaId }, token)
}

/** Shape EXACTO que devuelve la tool del back (63d9b187): mapCampo → {clave, tipo, valor}.
 *  `tipo` es el DATATYPE de la definición (String/Text/Number/Boolean/JSON/Enum/…), no el
 *  origen (resolucion.tipo). El front coerciona el input por este tipo antes del PATCH. */
export interface CampoInstancia {
  clave: string
  tipo?: string
  valor?: unknown
}
export interface CamposActor {
  instancia_id: string
  entidad: string
  campos_legibles?: CampoInstancia[]
  campos_editables?: CampoInstancia[]
}

/**
 * IHC-CAMPOS (orden 74043ee7): campos que ESTE actor puede LEER y EDITAR sobre la
 * instancia, filtrados por grant campo-UUID server-side (madre resolverCamposPorEntidad).
 * Tool gemela de get_available_actions, integrada por el back (63d9b187). El componente
 * pinta exactamente lo que la tool devuelva (cero hardcode); el enforce del PATCH es la
 * autoridad — la tool informa, no otorga.
 */
export function getAvailableFields(instanciaId: string, token: string | null): Promise<CamposActor | null> {
  return mcpToolCall<CamposActor>('get_available_fields', { instancia_id: instanciaId }, token)
}

/** Muestra de instancia que devuelve query_instances (id + estado + fase + payload). */
export interface InstanciaMuestra { id: string; estado?: string; current_state?: string; fase?: string; payload?: Record<string, unknown> }
export interface QueryInstanciasResp { total_real: number; muestra: InstanciaMuestra[] }

/**
 * #238 panel hoja-de-ruta: query_instances con filtro de payload (igualdad) sobre un campo con
 * índice §34 declarado. Habilita "órdenes WHERE plan_operacional_id = nodo" sin denormalizar ni
 * cargar todo el universo. RBAC/RLS server-side (el actor solo ve lo suyo). `estado` opcional
 * (current_state exacto) permite contar por estado sin traer las filas — p.ej. % de ejecución por
 * hito (aplicadas/total): `total_real` con estado='aplicada' / `total_real` sin estado.
 */
export function queryInstancesPorCampo(entidad: string, campo: string, valor: string, token: string | null, limit = 50, estado?: string): Promise<QueryInstanciasResp | null> {
  const args: Record<string, unknown> = { entidad, filtros_payload: [{ campo, valor }], limit }
  if (estado) args.estado = estado
  return mcpToolCall<QueryInstanciasResp>('query_instances', args, token)
}

/**
 * #168 — NEUROCÓRTEX (§33.4): resuelve palabras → UUID → entidad/capacidad vía la MISMA tool MCP
 * que usa el chat (resolver_capacidades), por POST /api/v1/mcp con el JWT del actor. NO hay
 * endpoint nuevo (sería mecanismo paralelo, drift §17). La búsqueda JAMÁS autoriza: devuelve los
 * candidatos del diccionario (la autoridad la computa §21 en el call al navegar/actuar).
 * Hoy resuelve entidad/fase/intención por tier exacto+léxico (pg_trgm); campos = deuda #107.
 */
export interface CandidatoResolucion {
  texto: string
  idioma?: string
  objetivo_uuid: string
  tipo_objetivo: string // 'entidad' | 'fase' | 'intencion'
  entidad_nombre: string | null
  ambito: 'co' | 'global'
  tipo_match: 'exacto' | 'lexico' | 'uuid'
  score: number
}
export interface RespuestaResolucion {
  candidatos: CandidatoResolucion[]
  tools?: unknown[]
  sugerencia?: string
}
export function resolverCapacidades(pregunta: string, token: string | null): Promise<RespuestaResolucion | null> {
  return mcpToolCall<RespuestaResolucion>('resolver_capacidades', { pregunta }, token)
}
